import { topupApplicationClient } from '@api'
import { TApplication } from '@api/types'
import { createModel } from '@rematch/core'
import { RootModel } from '@stores'
import { TPayFormState } from '@stores/types/TPayFormState'
import { extractMaxLimit, extractMinLimit } from '@utils'

import { TFullName } from '../../api/types/TFullName'
import { TPersonalData } from '../../api/types/TPersonalData'
import { TPaymentGateway } from '../types/TPaymentGatewayState'

export const SESSION_PHONE_KEY = 'SESSION_PHONE'
export const SESSION_SENDER_KEY = 'SESSION_SENDER'
export const SESSION_SENDER_FULL_NAME_KEY = 'SESSION_SENDER_FULL_NAME'
export const SESSION_RECEIVER_KEY = 'SESSION_RECEIVER'
export const SESSION_RECEIVER_BANK_CARD_KEY = 'SESSION_RECEIVER_BANK_CARD'
export const SESSION_LAST4PAN_KEY = 'SESSION_LAST4PAN'
export const SESSION_GW_KEY = 'SESSION_GW'
export const SESSION_PRODUCT_KEY = 'SESSION_PRODUCT'
export const SESSION_SOURCE_KEY = 'SESSION_SOURCE'
export const SESSION_RECEIVER_BANK_KEY = 'SESSION_RECEIVER_BANK'
export const SESSION_IMPS_ACCOUNT_KEY = 'SESSION_IMPS_ACCOUNT'
export const SESSION_IMPS_ACCOUNT_NUM_KEY = 'SESSION_IMPS_ACCOUNT_NUM'
export const SESSION_IMPS_IFSC_KEY = 'SESSION_IMPS_IFSC'
export const SESSION_RECEIVER_ACCOUNT_NUMBER_KEY =
  'SESSION_RECEIVER_ACCOUNT_NUMBER'
export const SESSION_RECEIVER_ACCOUNT_HOLDER_KEY =
  'SESSION_RECEIVER_ACCOUNT_HOLDER'

export const payForm = createModel<RootModel>()({
  state: {
    amountFrom: '0',
    amountTo: '0',
    paymentBlocking: false,
  } as TPayFormState,
  reducers: {
    set(state, payload: TPayFormState) {
      return { ...state, ...payload }
    },
    setIfEmpty(state, payload: TPayFormState) {
      return {
        ...state,
        ...Object.fromEntries(
          Object.entries(payload).map(([key, value]) => {
            return [key, state[key as keyof TPayFormState] ?? value]
          }),
        ),
      }
    },
  },
  effects: (dispatch) => ({
    init(payload: { application: TApplication }) {
      dispatch.payForm.set({
        ...payload,
      } as TPayFormState)
    },
    initBySession(gws: TPaymentGateway[]) {
      const sessionPhone = sessionStorage.getItem(SESSION_PHONE_KEY)
      if (sessionPhone) {
        dispatch.payForm.set({ phone: sessionPhone })
      }

      const sessionSenderFullName = sessionStorage.getItem(
        SESSION_SENDER_FULL_NAME_KEY,
      )
      if (sessionSenderFullName) {
        dispatch.payForm.set({
          senderFullName: JSON.parse(sessionSenderFullName) as TFullName,
        })
      }

      const sessionReceiver = sessionStorage.getItem(SESSION_RECEIVER_KEY)
      if (sessionReceiver) {
        dispatch.payForm.set({ receiver: sessionReceiver })
      }

      const sessionSender = sessionStorage.getItem(SESSION_SENDER_KEY)
      if (sessionSender) {
        dispatch.payForm.set({ sender: sessionSender })
      }

      const sessionLast4Pan = sessionStorage.getItem(SESSION_LAST4PAN_KEY)
      if (sessionLast4Pan) {
        dispatch.payForm.set({ last4pan: sessionLast4Pan })
      }

      const sessionReceiverBank = sessionStorage.getItem(
        SESSION_RECEIVER_BANK_KEY,
      )
      if (sessionReceiverBank) {
        dispatch.payForm.set({ receiverBank: JSON.parse(sessionReceiverBank) })
      }

      const sessionReceiverBankCard = sessionStorage.getItem(
        SESSION_RECEIVER_BANK_CARD_KEY,
      )
      if (sessionReceiverBankCard) {
        dispatch.payForm.set({ receiverBankCard: sessionReceiverBankCard })
      }

      const sessionImpsAccount = sessionStorage.getItem(
        SESSION_IMPS_ACCOUNT_KEY,
      )
      if (sessionImpsAccount) {
        dispatch.payForm.set({ impsAccount: sessionImpsAccount })
      }

      const sessionImpsAccountNum = sessionStorage.getItem(
        SESSION_IMPS_ACCOUNT_NUM_KEY,
      )
      if (sessionImpsAccountNum) {
        dispatch.payForm.set({ impsAccountNum: sessionImpsAccountNum })
      }

      const sessionImpsIfsc = sessionStorage.getItem(SESSION_IMPS_IFSC_KEY)
      if (sessionImpsIfsc) {
        dispatch.payForm.set({ ifsc: sessionImpsIfsc })
      }

      const sessionAccountNumber = sessionStorage.getItem(
        SESSION_RECEIVER_ACCOUNT_NUMBER_KEY,
      )
      if (sessionAccountNumber) {
        dispatch.payForm.set({ receiverAccountNumber: sessionAccountNumber })
      }

      const sessionAccountHolder = sessionStorage.getItem(
        SESSION_RECEIVER_ACCOUNT_HOLDER_KEY,
      )
      if (sessionAccountHolder) {
        dispatch.payForm.set({ receiverAccountHolder: sessionAccountHolder })
      }

      const sessionGwId = sessionStorage.getItem(SESSION_GW_KEY)
      const currentGw = gws.find(
        (value) => value.paymentGatewayId == sessionGwId,
      )
      if (currentGw) {
        dispatch.payForm.set({
          rate: currentGw.exchangeRate,
          minLimit: extractMinLimit(currentGw),
          maxLimit: extractMaxLimit(currentGw),
          paymentGateway: currentGw,
        })
      }
    },
    initPersonalData(payload: TPersonalData) {
      dispatch.payForm.setIfEmpty({
        sender: payload.sender_name,
        phone: payload.sbp_phone?.slice(1), // remove leads "7" for correct insert
        last4pan: payload.last4pan,
        receiver: payload.receiver_name,
        account: payload.account,
        // receiverBank: payload.nspk_bank_id, пока убрано, т.к. надо как-то сюда
        // прокинуть список всех банков чтоб найти правильный объект банка
        receiverBankCard: payload.to_account,
      })
    },
    async fetchExchanges() {
      const { exchanges } = await topupApplicationClient.getExchanges()
      dispatch.payForm.set({ exchanges })
    },
    async fetchUsersUSDTAddress() {
      const address = await topupApplicationClient.getUSDTAddress()
      dispatch.payForm.set({ address })
    },
  }),
})
